/**
 * Public intake embed form.
 * Used when someone opens the embed link (e.g. from a snippet on another site).
 * No auth required; submissions are routed to the queue associated with the token.
 */

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getSupabaseClient } from '../../lib/supabaseClient';
import { Send, CheckCircle, AlertCircle } from 'lucide-react';

export default function IntakeEmbedPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token.trim()) {
      setError('Missing embed token');
      return;
    }
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setError('Please enter a title');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const supabase = getSupabaseClient();
      const { error: rpcError } = await supabase.rpc('submit_via_embed_token', {
        p_token: token,
        p_title: trimmedTitle,
        p_description: description.trim() || null,
      });
      if (rpcError) {
        setError(rpcError.message || 'Submission failed');
        setSubmitting(false);
        return;
      }
      setSuccess(true);
      setTitle('');
      setDescription('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-[200px] flex items-center justify-center p-6 bg-[var(--surface)]">
        <div className="text-center text-[var(--text-sec)]">
          <AlertCircle className="h-10 w-10 mx-auto mb-2 text-[var(--mustard)]" />
          <p>Invalid or missing embed link. Use the link provided by your team.</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-[200px] flex items-center justify-center p-6 bg-[var(--surface)]">
        <div className="text-center">
          <CheckCircle className="h-12 w-12 mx-auto mb-3 text-[var(--green)]" />
          <h3 className="text-lg font-semibold text-[var(--text-pri)]">Thank you</h3>
          <p className="text-[var(--text-sec)] mt-1">Your idea has been submitted and will be reviewed.</p>
          <button
            type="button"
            onClick={() => setSuccess(false)}
            className="mt-4 px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 text-sm"
          >
            Submit another
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[200px] p-6 bg-[var(--surface)]">
      <form onSubmit={handleSubmit} className="max-w-md mx-auto space-y-4">
        <h2 className="text-lg font-semibold text-[var(--text-pri)]">Submit an idea</h2>
        {error && (
          <div className="p-3 bg-[var(--coral-dim)] border border-[var(--coral)] text-sm text-[var(--coral)] flex items-center gap-2">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            {error}
          </div>
        )}
        <div>
          <label htmlFor="embed-title" className="block text-sm font-medium text-[var(--text-sec)] mb-1">
            Title <span className="text-[var(--coral)]">*</span>
          </label>
          <input
            id="embed-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Brief summary of your idea"
            className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-primary-500 focus:border-primary-500"
            maxLength={500}
            required
          />
        </div>
        <div>
          <label htmlFor="embed-description" className="block text-sm font-medium text-[var(--text-sec)] mb-1">
            Description (optional)
          </label>
          <textarea
            id="embed-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="More context or details..."
            rows={3}
            className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-primary-500 focus:border-primary-500"
            maxLength={5000}
          />
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? (
            'Submitting…'
          ) : (
            <>
              <Send className="h-4 w-4" />
              Submit
            </>
          )}
        </button>
      </form>
    </div>
  );
}
